'use client';

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

interface AppearanceReviewStepProps {
  aiAnalysis: {
    gender?: string;
    age?: number;
    hairColor?: string;
    hairStyle?: string;
    skinColor?: string;
    confidence?: number;
  };
  onUpdate: (updates: Partial<AppearanceReviewStepProps['aiAnalysis']>) => void;
  onContinue: () => void;
}

export default function AppearanceReviewStep({ 
  aiAnalysis, 
  onUpdate, 
  onContinue 
}: AppearanceReviewStepProps) {
  // Debug: Track render count
  const renderCount = useRef(0);
  renderCount.current++;
  console.log(`👤 [APPEARANCE-REVIEW] Render #${renderCount.current} - aiAnalysis:`, aiAnalysis);
  
  // Track mount/unmount
  useEffect(() => {
    console.log(`👤 [APPEARANCE-REVIEW-MOUNT] Component mounted at render #${renderCount.current}`);
    return () => {
      console.log(`👤 [APPEARANCE-REVIEW-UNMOUNT] Component unmounting after render #${renderCount.current}`);
    };
  }, []);
  
  // Initialize with aiAnalysis data directly to prevent useEffect loops
  const [appearance, setAppearance] = useState(() => ({
    gender: aiAnalysis?.gender ? aiAnalysis.gender.toLowerCase() : 'other',
    hairColor: aiAnalysis?.hairColor || 'brown',
    hairStyle: aiAnalysis?.hairStyle || 'short',
    skinTone: aiAnalysis?.skinColor || 'medium'
  }));

  // Remove the problematic useEffect that was causing React Error #310
  // The initialization is now handled in useState initializer above

  // Gender options with emojis
  const genderOptions = [
    { id: 'male', label: 'Boy', emoji: '👦' },
    { id: 'female', label: 'Girl', emoji: '👧' },
    { id: 'other', label: 'Other', emoji: '🌟' }
  ];

  // Hair color options with visual swatches
  const hairColors = [
    { id: 'black', color: '#1a1a1a', label: 'Black' },
    { id: 'brown', color: '#6B4423', label: 'Brown' },
    { id: 'blonde', color: '#F4D03F', label: 'Blonde' },
    { id: 'red', color: '#B73E3E', label: 'Red' },
    { id: 'gray', color: '#9CA3AF', label: 'Gray' },
    { id: 'other', color: 'linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1)', label: 'Other' }
  ];

  // Hair style options with icons
  const hairStyles = [
    { id: 'short', label: 'Short', icon: '✂️' },
    { id: 'medium', label: 'Medium', icon: '💇' },
    { id: 'long', label: 'Long', icon: '💁' },
    { id: 'curly', label: 'Curly', icon: '🌊' },
    { id: 'straight', label: 'Straight', icon: '📏' },
    { id: 'braided', label: 'Braided', icon: '🎀' }
  ];

  // Skin tone options with gradient
  const skinTones = [
    { id: 'light', color: '#FDEBD0', label: 'Light' },
    { id: 'medium-light', color: '#F5DEB3', label: 'Medium Light' },
    { id: 'medium', color: '#D2B48C', label: 'Medium' },
    { id: 'medium-dark', color: '#BC8F8F', label: 'Medium Dark' },
    { id: 'dark', color: '#8B4513', label: 'Dark' }
  ];

  const handleUpdate = (field: string, value: string) => {
    const newAppearance = { ...appearance, [field]: value };
    setAppearance(newAppearance);
    onUpdate({ [field]: value });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {aiAnalysis.confidence && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-4"
        >
          <p className="text-sm text-green-600">
            Detection Confidence: {Math.round(aiAnalysis.confidence * 100)}%
          </p>
        </motion.div>
      )}

      <div className="space-y-8">
        {/* Gender Selection */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Gender</h3>
          <div className="flex flex-wrap gap-4">
            {genderOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleUpdate('gender', option.id)}
                className={`relative px-8 py-4 rounded-xl text-lg font-medium transition-all ${
                  appearance.gender === option.id
                    ? 'bg-purple-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-300'
                }`}
              >
                <span className="text-2xl mr-2">{option.emoji}</span>
                {option.label}
                {appearance.gender === option.id && (
                  <Check className="absolute top-2 right-2 w-5 h-5" />
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Hair Color Selection */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Hair Color</h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {hairColors.map((color) => (
              <button
                key={color.id}
                onClick={() => handleUpdate('hairColor', color.id)}
                className={`relative p-4 rounded-xl transition-all ${
                  appearance.hairColor === color.id
                    ? 'ring-4 ring-purple-600 shadow-lg scale-105'
                    : 'hover:scale-105'
                }`}
              >
                <div
                  className="w-16 h-16 mx-auto rounded-full shadow-inner mb-2"
                  style={{
                    background: color.color.includes('gradient') ? color.color : color.color,
                    backgroundColor: !color.color.includes('gradient') ? color.color : undefined
                  }}
                />
                <p className="text-sm font-medium text-gray-700">{color.label}</p>
                {appearance.hairColor === color.id && (
                  <Check className="absolute top-2 right-2 w-5 h-5 text-purple-600" />
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Hair Style Selection */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Hair Style</h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {hairStyles.map((style) => (
              <button
                key={style.id}
                onClick={() => handleUpdate('hairStyle', style.id)}
                className={`relative p-4 rounded-xl transition-all ${
                  appearance.hairStyle === style.id
                    ? 'bg-purple-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="text-3xl mb-2">{style.icon}</div>
                <p className="text-sm font-medium">{style.label}</p>
                {appearance.hairStyle === style.id && (
                  <Check className="absolute top-2 right-2 w-5 h-5" />
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Skin Tone Selection */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Skin Tone</h3>
          <div className="flex flex-wrap gap-4 justify-center">
            {skinTones.map((tone) => (
              <button
                key={tone.id}
                onClick={() => handleUpdate('skinTone', tone.id)}
                className={`relative p-2 rounded-xl transition-all ${
                  appearance.skinTone === tone.id
                    ? 'ring-4 ring-purple-600 shadow-lg scale-105'
                    : 'hover:scale-105'
                }`}
              >
                <div
                  className="w-20 h-20 rounded-lg shadow-inner"
                  style={{ backgroundColor: tone.color }}
                />
                <p className="text-xs font-medium text-gray-700 mt-2">{tone.label}</p>
                {appearance.skinTone === tone.id && (
                  <Check className="absolute top-1 right-1 w-5 h-5 text-purple-600 bg-white rounded-full p-0.5" />
                )}
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
